import Cookies from 'universal-cookie';
import useApi from 'hooks/useApi';
import { ThemeMode } from 'config';
import { LAYOUT_FONT_SIZE_MAX_PX, LAYOUT_FONT_SIZE_MIN_PX, LAYOUT_PREFS_SESSION_CACHE_PREFIX } from 'constants/layoutPreferences';

function clampFontSizePx(value) {
  if (value == null || value === '') return null;
  const v = Math.round(Number(value));
  if (Number.isNaN(v)) return null;
  return Math.min(LAYOUT_FONT_SIZE_MAX_PX, Math.max(LAYOUT_FONT_SIZE_MIN_PX, v));
}

/**
 * GET user customize settings (theme, language, font size). No side effects.
 * @returns Partial config fields to merge into local layout state, or null if unavailable.
 */
export async function fetchCustomizeLayoutPreferences() {
  const cookies = new Cookies();
  if (!cookies.get('_Id')) {
    return null;
  }
  try {
    const result = await useApi('GET_LAYOUT_STATUS', {}, {});
    const ok = result?.response?.status === true || result?.response?.status === 'true';
    if (!ok) return null;

    let details = result?.details;
    if (!details || typeof details !== 'object') return null;

    if (
      (details.customConfig != null && typeof details.customConfig === 'object') ||
      (details.defConfig != null && typeof details.defConfig === 'object')
    ) {
      const flat = {
        ...(typeof details.defConfig === 'object' ? details.defConfig : {}),
        ...(typeof details.customConfig === 'object' ? details.customConfig : {})
      };
      if (Object.keys(flat).length) details = flat;
    }

    const patch = {};
    if (details.themeStyle != null && details.themeStyle !== '') {
      patch.mode = String(details.themeStyle) === '1' ? ThemeMode.DARK : ThemeMode.LIGHT;
    }
    const rawLang = details.userLang;
    if (rawLang != null && String(rawLang).trim() !== '') {
      patch.i18n = String(rawLang).trim();
    }
    if (details.fontSize != null && details.fontSize !== '') {
      const font = clampFontSizePx(details.fontSize);
      if (font != null) {
        patch.fontSize = font;
      }
    }
    return Object.keys(patch).length ? patch : null;
  } catch {
    return null;
  }
}

function layoutPrefsSessionCacheKey(userId) {
  return `${LAYOUT_PREFS_SESSION_CACHE_PREFIX}${userId}`;
}

/**
 * Synchronous read of the session customize cache for one user.
 * @returns `undefined` if nothing cached; otherwise the parsed patch (may be `null`).
 */
export function getLayoutPrefsSessionCachedPatch(insightUserId) {
  if (insightUserId == null || insightUserId === '') return undefined;
  try {
    const hit = sessionStorage.getItem(layoutPrefsSessionCacheKey(String(insightUserId)));
    if (hit === null) return undefined;
    return JSON.parse(hit);
  } catch {
    return undefined;
  }
}

/** Writes the session customize cache for one user (including `null` patch). */
export function setLayoutPrefsSessionCachedPatch(insightUserId, patch) {
  if (insightUserId == null || insightUserId === '') return;
  try {
    sessionStorage.setItem(layoutPrefsSessionCacheKey(String(insightUserId)), JSON.stringify(patch));
  } catch {
    // private mode / disabled storage
  }
}

/** Serializes customize POSTs so a slower request cannot finish after a newer one and overwrite server state. */
let customizeSaveTail = Promise.resolve();

function resetCustomizeSaveQueue() {
  customizeSaveTail = Promise.resolve();
}

/**
 * Clears cached customize GET for one user, or all cached users if `insightUserId` is omitted.
 * Also resets the customize POST queue so in-flight work from a prior session cannot delay saves
 * after logout or account switch (see `saveLayoutPreferences`).
 */
export function clearLayoutPreferencesSessionCache(insightUserId) {
  resetCustomizeSaveQueue();
  try {
    if (insightUserId != null && insightUserId !== '') {
      sessionStorage.removeItem(layoutPrefsSessionCacheKey(String(insightUserId)));
      return;
    }
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(LAYOUT_PREFS_SESSION_CACHE_PREFIX)) {
        sessionStorage.removeItem(k);
      }
    }
  } catch {
    // private mode / disabled storage
  }
}

/**
 * One network customize GET per browser tab session per `_Id`; reuses sessionStorage until
 * `clearLayoutPreferencesSessionCache` (logout).
 * For an explicit refetch (e.g. manual refresh), call `fetchCustomizeLayoutPreferences` instead.
 */
export async function fetchCustomizeLayoutPreferencesOncePerSession() {
  const cookies = new Cookies();
  const id = cookies.get('_Id');
  if (!id) {
    return null;
  }
  const cached = getLayoutPrefsSessionCachedPatch(id);
  if (cached !== undefined) {
    return cached;
  }
  const patch = await fetchCustomizeLayoutPreferences();
  setLayoutPrefsSessionCachedPatch(id, patch);
  return patch;
}

/** POST setCustomizeSettings. */
export function saveLayoutPreferences(config) {
  const cookies = new Cookies();
  const userId = cookies.get('_Id');
  if (!userId) return;

  const customConfig = {
    themeStyle: config.mode === ThemeMode.DARK ? '1' : '0',
    userLang: config.i18n || 'en'
  };
  const font = clampFontSizePx(config.fontSize);
  if (font != null) {
    customConfig.fontSize = font;
  }

  // Keep session cache in sync so a tab refresh doesn't reapply stale prefs.
  setLayoutPrefsSessionCachedPatch(userId, {
    mode: config.mode,
    i18n: customConfig.userLang,
    ...(font != null ? { fontSize: font } : {})
  });

  customizeSaveTail = customizeSaveTail
    .catch(() => {})
    .then(async () => {
      const currentUserId = new Cookies().get('_Id');
      if (!currentUserId || currentUserId !== userId) return;
      try {
        await useApi('SET_LAYOUT_STATUS', {}, { data: { customConfig } });
      } catch {
        // non-blocking
      }
    });
}
